import type * as React from "react";
import {
  AiIllustration,
  ConfigManagerIllustration,
  DatasourcesIllustration,
  DragAndDropIllustration,
  EmailBuilderIllustration,
  ProcessAutomationIllustration,
  ReportDesignerIllustration,
  ScheduledJobsIllustration
} from "./banner-illustrations";

/** The two `KuikaSvg` sprite sheets the banner's pills draw from. */
export type AuthenticationBannerIconType = "common" | "topbar-icons";

/**
 * A miniature of one designer tool. Takes no props, and must contain no focusable
 * element: the viewport that hosts it is `aria-hidden`, and `aria-hidden` over a
 * focusable node hides it from assistive tech while leaving it in the tab order.
 */
export type AuthenticationBannerIllustration = React.FC;

export interface AuthenticationBannerFeature {
  /** Stable React key and test handle. Never rendered. */
  id: string;
  /** The full name. The pill's accessible name, and the caption above the viewport. */
  label: string;
  /**
   * What fits a quarter-width pill at 11px — roughly 17 characters once the icon and the
   * padding are taken off the 576px of card content. It MUST stay a substring of `label`
   * so the visible text is contained in the accessible name (WCAG 2.5.3 Label in Name);
   * a test enforces it.
   */
  shortLabel: string;
  /** One line on what the tool is for, read beside the label above the illustration. */
  note: string;
  iconName: string;
  iconType: AuthenticationBannerIconType;
  Illustration: AuthenticationBannerIllustration;
}

/**
 * The order is the order the slider advances through, and the order the pills sit in the
 * two-row strip. It opens on Drag-and-drop because that is the act the whole product is
 * built around.
 */
export const AUTHENTICATION_BANNER_FEATURES: readonly AuthenticationBannerFeature[] = [
  {
    id: "drag-and-drop",
    label: "Drag-and-drop",
    shortLabel: "Drag-and-drop",
    note: "Place components on the canvas and arrange them by hand.",
    iconName: "drag-drop",
    iconType: "common",
    Illustration: DragAndDropIllustration
  },
  {
    id: "datasources",
    label: "Connect to Datasources",
    shortLabel: "Datasources",
    note: "SQL Server, PostgreSQL, REST, SAP — mapped once, queried everywhere.",
    iconName: "datasources",
    iconType: "topbar-icons",
    Illustration: DatasourcesIllustration
  },
  {
    // "Process Automation" is what the tool is called everywhere else in the product —
    // the topbar icon, the landing page, the search placeholder. The pill shows the
    // second word because the pair does not fit a quarter-width pill at 11px.
    id: "process-automation",
    label: "Process Automation",
    shortLabel: "Automation",
    note: "Model approvals and long-running processes as BPMN.",
    iconName: "process-automation",
    iconType: "topbar-icons",
    Illustration: ProcessAutomationIllustration
  },
  {
    id: "ai",
    label: "AI",
    shortLabel: "AI",
    note: "Describe the screen; watch it get built, step by step.",
    iconName: "ai-hub",
    iconType: "topbar-icons",
    Illustration: AiIllustration
  },
  {
    id: "email-builder",
    label: "Email Builder",
    shortLabel: "Email Builder",
    note: "Design transactional emails, in every language you ship.",
    iconName: "email-builder",
    iconType: "topbar-icons",
    Illustration: EmailBuilderIllustration
  },
  {
    id: "report-designer",
    label: "Report Designer",
    shortLabel: "Report Designer",
    note: "Lay out real pages — the PDF is what you see.",
    iconName: "report-designer",
    iconType: "topbar-icons",
    Illustration: ReportDesignerIllustration
  },
  {
    id: "scheduled-jobs",
    label: "Scheduled Jobs",
    shortLabel: "Scheduled Jobs",
    note: "Run work on a schedule, in the timezone you choose.",
    iconName: "scheduled-jobs",
    iconType: "topbar-icons",
    Illustration: ScheduledJobsIllustration
  },
  {
    id: "config-manager",
    label: "Configuration Manager",
    shortLabel: "Configuration",
    note: "One configuration per environment, across every platform.",
    iconName: "config-manager",
    iconType: "topbar-icons",
    Illustration: ConfigManagerIllustration
  }
];
